import { Avatar, Box, Button, Center, Flex, FlexProps, IconButton, Text, Textarea } from '@chakra-ui/react';
import { NpsTag } from '@outdoorly/chat-sdk';
import { PossibleEnrichments, useSendMessage } from '@outdoorly/react-chat-sdk';
import posthog from 'posthog-js';
import React, { useEffect, useRef, useState } from 'react';
import Emoji from 'react-emoji-render';
import { MotionBox } from '../../../components/Motion';
import { Store } from '../../../store';
import { useMemoizedFrame } from '../../../utils/hooks';
import { DashedFlex } from '../MessageEvent/DashedFlex';

type NetPromoterScoreProps = {
  npsRequest?: PossibleEnrichments['npsRequest'];
  npsRequestId?: string;
  skipped: boolean;
};

export const NetPromoterScore: React.FC<NetPromoterScoreProps> = React.memo(
  (props: NetPromoterScoreProps) => {
    const { npsRequest, npsRequestId, skipped } = props;

    const [isOpen, setIsOpen] = useState(false);
    const [rating, setRating] = useState(typeof npsRequest?.rating === 'number' ? npsRequest.rating : null);
    const [focusable, setFocusable] = useState(false);
    useEffect(() => {
      if (typeof npsRequest?.rating === 'number') setRating(npsRequest?.rating);
      if (npsRequest && npsRequest.expert && !npsRequest.completed && !npsRequest.skipped) {
        setFocusable(true);
      } else if (npsRequest && (npsRequest.completed || npsRequest.skipped) && focusable) {
        setFocusable(false);
      }
    }, [npsRequest]);
    const expert = npsRequest?.expert;
    const avatarUrl = expert?.avatar;
    const labels = Store.useState(
      (s) =>
        s.labels.substitute({
          expertName: expert?.publicName ?? '',
          rating: rating === 0 ? s.labels.badRating : rating === 1 ? s.labels.okayRating : s.labels.goodRating
        }),
      [expert?.publicName, rating]
    );
    const messageSender = useSendMessage();

    if (isOpen)
      return (
        <NetPromoterScoreOpen
          npsRequest={npsRequest}
          npsRequestId={npsRequestId}
          rating={rating}
          setRating={setRating}
          setIsOpen={setIsOpen}
        />
      );

    if (typeof rating === 'number') {
      return (
        // Position relative here is important, as it resolves a scrolling bug in iOS Safari
        // without it, child elements jump around the screen if the parent is shifted upward
        // if a later element grows in size.
        <Flex position="relative" align="center" flexDir="column" my="12px" role="row">
          <Flex flexDir="row" align="center">
            {avatarUrl && (
              <Avatar
                src={avatarUrl}
                w="24px"
                h="24px"
                mr="4px"
                ignoreFallback
                role="presentation"
                name={expert?.publicName}
              />
            )}
            <Text fontSize="14px" lineHeight="14px">
              <Emoji text=":speech_balloon:" />
              <Emoji text={rating === 0 ? ':tired_face:' : rating === 1 ? ':neutral_face:' : ':grin:'} />
            </Text>
          </Flex>

          <DashedFlex role="gridcell" tabIndex={-1}>
            <Text variant="label-medium" color="scheme.contentSecondary" textAlign="center" maxW="80%">
              {labels.youRatedThisConvo}
            </Text>
          </DashedFlex>
          <Text
            color="scheme.contentTertiary"
            variant="label-small"
            textDecoration="underline"
            onClick={() => {
              posthog.capture('EDIT-NPS');
              setIsOpen(true);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                posthog.capture('EDIT-NPS');
                setIsOpen(true);
              }
            }}
            cursor="pointer"
            aria-label={`${labels.editReview}, button`}
            role="gridcell"
            tabIndex={-1}
          >
            {labels.editReview}
          </Text>
        </Flex>
      );
    }

    return (
      <MotionBox
        position="relative"
        background="scheme.backgroundPrimary"
        borderRadius="0 0 12px 12px"
        sx={{
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}
      >
        <NpsEmojiSelector
          selectedRating={rating}
          onSelect={(selectedRating) => {
            if (selectedRating !== rating) {
              setRating(selectedRating);
              setIsOpen(true);
            }
          }}
          flexProps={{
            mt: '32px'
          }}
          parentFocusable={focusable}
        />
        {rating === null ? (
          <Text variant="label-small" textAlign="center" my="8px" color="scheme.contentTertiary">
            {labels.selectReaction}
          </Text>
        ) : (
          <Text variant="label-small" textAlign="center" mt="8px" color="scheme.contentTertiary">
            {rating === 0 ? labels.badRating : rating === 1 ? labels.okayRating : labels.goodRating}
          </Text>
        )}
        {!skipped && typeof npsRequest?.rating !== 'number' && (
          <Center mt="32px" mb="16px">
            <Button
              variant="chat-option"
              onClick={() => {
                posthog.capture('SKIP-NPS');
                messageSender({ type: 'nps', id: npsRequestId ?? '' });
              }}
              aria-label="skip rating the conversation for now"
            >
              {labels.skipNPS}
            </Button>
          </Center>
        )}
      </MotionBox>
    );
  }
);

type NetPromoterScoreOpenProps = Omit<NetPromoterScoreProps, 'skipped'> & {
  rating?: number | null;
  setRating: (rating: number | null) => void;
  setIsOpen: (isOpen: boolean) => void;
};

const NetPromoterScoreOpen: React.FC<NetPromoterScoreOpenProps> = ({
  npsRequest,
  npsRequestId,
  rating,
  setRating,
  setIsOpen
}) => {
  const [additionalNotes, setAdditionalNotes] = useState(npsRequest?.additionalNotes ?? '');
  const [tag, setTag] = useState<NpsTag | null>(npsRequest?.tag ? npsRequest.tag : null);

  useEffect(() => {
    if (npsRequest?.additionalNotes) setAdditionalNotes(npsRequest?.additionalNotes);
    if (npsRequest?.tag) setTag(npsRequest.tag);
  }, [npsRequest]);

  // If rating changes, reset the chosen tag.
  useEffect(() => {
    if (rating !== npsRequest?.rating) {
      setTag(null);
    }
  }, [npsRequest?.rating, rating]);

  const expert = npsRequest?.expert;
  const avatarUrl = expert?.avatar;
  const expertName = expert?.publicName;
  const messageSender = useSendMessage();

  const labels = Store.useState(
    (s) =>
      s.labels.substitute({
        expertName: expert?.publicName ?? '',
        rating: rating === 0 ? s.labels.badRating : rating === 1 ? s.labels.okayRating : s.labels.goodRating
      }),
    [expert?.publicName, rating]
  );

  return (
    <Box
      pos={'absolute'}
      height={'calc(100% - 66px)'}
      background="scheme.backgroundPrimary"
      top="66px"
      left="0px"
      width="100%"
      zIndex="modal"
      role="dialog"
      borderRadius="0 0 12px 12px"
      overflow="scroll"
      sx={{
        '&::-webkit-scrollbar': {
          display: 'none'
        },
        scrollbarWidth: 'none',
        msOverflowStyle: 'none'
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          setIsOpen(false);
          posthog.capture('CANCEL-NPS');
          if (typeof npsRequest?.rating !== 'number') setRating(null);
          // Reset the rating on cancel.
          else setRating(npsRequest?.rating);
        }
      }}
    >
      <MotionBox
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0, transition: { duration: 0, delay: 0 } }}
        key={`nps-header-${npsRequestId}`}
      >
        <Center mt="22px" justifyItems="center">
          {avatarUrl && (
            <Avatar src={avatarUrl} w="24px" h="24px" mr="4px" ignoreFallback role="presentation" />
          )}
          <Text fontSize="14px">
            <Emoji text=":speech_balloon:" />
          </Text>
        </Center>
        {expertName && (
          <Text mt="12px" variant="label-large" textAlign="center" aria-live="polite">
            {labels.howWasYourConvo}
          </Text>
        )}
      </MotionBox>

      <NpsEmojiSelector
        selectedRating={rating}
        onSelect={(selectedRating) => {
          if (selectedRating !== rating) {
            setRating(selectedRating);
            setIsOpen(true);
            setTag(null);
          }
        }}
        flexProps={{
          mt: '16px'
        }}
        buttonsFocusable={true}
        parentFocusable={false}
      />
      {rating === null ? (
        <Text variant="label-small" textAlign="center" my="8px" color="scheme.contentTertiary">
          {labels.selectReaction}
        </Text>
      ) : (
        <Text variant="label-small" textAlign="center" mt="8px" color="scheme.contentTertiary">
          {rating === 0 ? labels.badRating : rating === 1 ? labels.okayRating : labels.goodRating}
        </Text>
      )}

      <MotionBox
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0, transition: { duration: 0, delay: 0 } }}
        key={`nps-details-${npsRequestId}`}
      >
        <Text my="12px" variant="label-large" textAlign="center" id="remarkRatingPrompt">
          {rating === 2 ? labels.whatWentWell : rating === 1 ? labels.whatWentOkay : labels.whatWentPoorly}
        </Text>
        <Center flexWrap="wrap" mx="10px" role="radiogroup" aria-labelledby="remarkRatingPrompt">
          <ConvoButtons
            tag={tag}
            setTag={setTag}
            availableTags={
              rating === 0 || rating === 1
                ? [
                    NpsTag.Condescending,
                    NpsTag.DidntConsiderBudget,
                    NpsTag.DidntGetEnoughInformation,
                    NpsTag.DidntHaveWhatIWasLookingFor,
                    NpsTag.DidntLikeProduct,
                    NpsTag.ExpertNotKnowledgeable,
                    NpsTag.Impersonal,
                    NpsTag.IrrelevantRecommendations,
                    NpsTag.Pushy,
                    NpsTag.Unprofessional,
                    NpsTag.WaitedTooLong
                  ]
                : [
                    NpsTag.ChattingToolEasyToUse,
                    NpsTag.ExpertListened,
                    NpsTag.ExpertWasKnowledgeable,
                    NpsTag.Friendly,
                    NpsTag.QualityRecommendations,
                    NpsTag.RespondedQuickly
                  ]
            }
          />
        </Center>
        <Text textAlign="center" variant="label-large" mt="22px">
          {labels.anythingToAddNPS}
        </Text>
        <Box p="12px 12px 0 12px">
          <Textarea
            variant="in-chat-textarea"
            placeholder={labels.anythingToAddNPSPlaceholder}
            value={additionalNotes}
            onChange={(e) => setAdditionalNotes(e.target.value)}
          />
        </Box>
        <Box p="12px" bottom="0px" w="100%">
          <Flex align="center">
            <Button
              variant="chat-option"
              mr="12px"
              onClick={() => {
                setIsOpen(false);
                posthog.capture('CANCEL-NPS');
                if (typeof npsRequest?.rating !== 'number') setRating(null);
                // Reset the rating on cancel.
                else setRating(npsRequest?.rating);
              }}
            >
              {labels.cancelNPSButton}
            </Button>
            <Button
              variant="submit-cta"
              w="100%"
              onClick={() => {
                if (typeof rating === 'number')
                  messageSender({
                    type: 'nps',
                    rating,
                    id: npsRequestId ?? '',
                    additionalNotes,
                    tag: tag ?? undefined
                  });
                posthog.capture('SUBMIT-NPS', {
                  rating,
                  additional_notes: additionalNotes,
                  tag
                });
                setIsOpen(false);
              }}
            >
              {labels.submitReview}
            </Button>
          </Flex>
        </Box>
      </MotionBox>
    </Box>
  );
};

type NpsEmojiSelectorProps = {
  selectedRating?: number | null;
  onSelect: (rating: number) => void;
  flexProps?: FlexProps;
  parentFocusable: boolean;
  buttonsFocusable?: boolean;
};

const NpsEmojiSelector: React.FC<NpsEmojiSelectorProps> = ({
  selectedRating,
  onSelect,
  flexProps,
  parentFocusable,
  buttonsFocusable
}) => {
  const ratingContainerRef = useRef<null | HTMLDivElement>(null);
  const [focusable, setFocusable] = useState(buttonsFocusable ?? false);
  const poorRatingButtonRef = useRef<null | HTMLButtonElement>(null);
  const { document } = useMemoizedFrame();
  return (
    <Center role="row">
      <Flex
        background="scheme.backgroundSecondary"
        p="6px"
        borderRadius="50px"
        tabIndex={parentFocusable ? 0 : -1}
        role="gridcell"
        ref={ratingContainerRef}
        aria-label="Rate your conversation buttons"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && document?.activeElement === ratingContainerRef.current) {
            poorRatingButtonRef.current?.focus();
            e.preventDefault();
            setFocusable(true);
          } else if (e.key === 'Escape') {
            ratingContainerRef?.current?.focus();
            setFocusable(false);
          }
        }}
        {...flexProps}
      >
        <Box role="radiogroup">
          <NpsEmojiButton
            emoji=":tired_face:"
            isSelected={selectedRating === 0}
            onSelect={() => onSelect(0)}
            aria-label="Rate your conversation poor"
            focusable={focusable}
            poorRatingButtonRef={poorRatingButtonRef}
          />
          <NpsEmojiButton
            emoji=":neutral_face:"
            aria-label="Rate your conversation okay"
            focusable={focusable}
            isSelected={selectedRating === 1}
            onSelect={() => onSelect(1)}
          />
          <NpsEmojiButton
            emoji=":grin:"
            isSelected={selectedRating === 2}
            onSelect={() => onSelect(2)}
            aria-label="Rate your conversation great"
            focusable={focusable}
          />
        </Box>
      </Flex>
    </Center>
  );
};

type NpsEmojiButtonProps = {
  emoji: string;
  onSelect: () => void;
  isSelected: boolean;
  'aria-label': string;
  focusable: boolean;
  poorRatingButtonRef?: React.MutableRefObject<HTMLButtonElement | null>;
};

const NpsEmojiButton: React.FC<NpsEmojiButtonProps> = (props) => {
  const { emoji, isSelected, onSelect, poorRatingButtonRef, focusable } = props;

  return (
    <IconButton
      icon={<Emoji text={emoji} />}
      aria-label={props['aria-label']}
      fontSize="40px"
      ref={poorRatingButtonRef}
      m={0}
      role="radio"
      aria-checked={isSelected}
      tabIndex={focusable ? 0 : -1}
      sx={{
        '& > span > span': {
          margin: '0px !important',
          verticalAlign: '0px !important',
          height: '40px !important',
          width: '40px !important'
        }
      }}
      onClick={() => onSelect()}
      borderRadius="50%"
      w="50px"
      minW="50px"
      h="50px"
      lineHeight="40px"
      mx="3px"
      background={isSelected ? 'scheme.whiteCompatible' : 'none'}
      _focus={{ outline: 0 }}
      _focusVisible={{ background: 'scheme.backgroundPrimary' }}
      _active={{ background: 'scheme.backgroundPrimary' }}
      _hover={{ background: isSelected ? 'scheme.whiteCompatible' : 'scheme.backgroundPrimary' }}
    />
  );
};

type ConvoButtonsProps = {
  tag: NpsTag | null;
  setTag: (arg0: NpsTag | null) => void;
  availableTags: Array<NpsTag>;
};

const ConvoButtons: React.FC<ConvoButtonsProps> = (props) => {
  const { tag, setTag, availableTags } = props;
  const labels = Store.useState((s) => s.labels);
  return (
    <>
      {availableTags.map((availableTag) => (
        <Button
          key={availableTag}
          variant={availableTag === tag ? 'selected-nps-option' : 'chat-option'}
          m="2px"
          role="radio"
          onClick={() => (availableTag === tag ? setTag(null) : setTag(availableTag))}
        >
          {labels.NPSResponseLabels[availableTag]}
        </Button>
      ))}
    </>
  );
};
